import React, { useMemo } from 'react';
import theme from './OptionGroupAccordion.module.scss';
import { LinkingType, OptionGroupDto, OptionGroupLinkStatus } from '@/types';
import { Button, ButtonBar, Dialog } from '@ygtechnologies/rubus-ui';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useQuery } from '@tanstack/react-query';
import { Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { useDeleteOptionGroup } from '@/services/options/option-groups.hooks';
import { searchDependencies, DependencyGroupDto } from '@/services/dependencies';
import { getOptionPresetGroupsWithItems } from '@/services/option-preset/option-preset-group';
import { OptionPresetGroupWithItemsDto } from '@/services/option-preset/option-preset-group/option-preset-group.types';

interface OptionGroupDeleteDialogProps {
    optionGroup: OptionGroupDto;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onDeleted: () => void;
}

export const OptionGroupDeleteDialog: React.FC<OptionGroupDeleteDialogProps> = ({
    optionGroup,
    open,
    onOpenChange,
    onDeleted,
}) => {
    const deleteOptionGroup = useDeleteOptionGroup();

    const { data: dependencyGroups, isFetching: dependenciesLoading } = useQuery<DependencyGroupDto[]>({
        queryKey: ['optionGroupDependencies', optionGroup.projectId],
        queryFn: () => searchDependencies({ projectId: optionGroup.projectId }),
        enabled: open,
    });
    const { data: presetGroups, isFetching: stylesLoading } = useQuery<OptionPresetGroupWithItemsDto[]>({
        queryKey: ['optionPresetGroupsWithItems', optionGroup.projectId],
        queryFn: () => getOptionPresetGroupsWithItems(optionGroup.projectId),
        enabled: open,
    });

    const isPublished =
        optionGroup.linkType === LinkingType.Direct
            ? optionGroup.linkedUnits.some((x) => x.status === OptionGroupLinkStatus.Approved)
            : optionGroup.linkedRooms.some((x) => x.status === OptionGroupLinkStatus.Approved);

    const dependenciesUsingGroup = useMemo(
        () =>
            (dependencyGroups ?? []).filter(
                (x) => x.fromOptionGroupId === optionGroup.id || x.toOptionGroupId === optionGroup.id
            ),
        [dependencyGroups, optionGroup.id]
    );

    const stylesUsingGroup = useMemo(() => {
        const stylesMap = new Map<string, { groupName: string; presetName: string }>();
        (presetGroups ?? []).forEach((presetGroup) => {
            presetGroup.presets.forEach((preset) => {
                (preset.presetItems ?? []).forEach((item) => {
                    if (item.optionGroupId === optionGroup.id) {
                        const key = `${presetGroup.name}::${preset.name}`;
                        stylesMap.set(key, { groupName: presetGroup.name, presetName: preset.name });
                    }
                });
            });
        });

        return Array.from(stylesMap.values());
    }, [presetGroups, optionGroup.id]);

    const handleDelete = () => {
        if (isPublished) {
            Toast.error(t`Option group is published. Unpublish before deleting.`);
            onOpenChange(false);
            return;
        }

        deleteOptionGroup.mutate(optionGroup.id, {
            onSuccess: () => {
                onOpenChange(false);
                onDeleted();
                Toast.success(t`Option group deleted`);
            },
            onError: async (err: any) => {
                onOpenChange(false);
                let message = t`Unable to delete option group`;
                if (err instanceof Response) {
                    try {
                        const data = await err.json();
                        message = data?.error ?? message;
                    } catch {
                        try {
                            const txt = await err.text();
                            message = txt || message;
                        } catch {}
                    }
                }
                Toast.error(message);
            },
        });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <Dialog.Content contentWidth="md">
                <Dialog.Header>
                    <Trans>Delete option group</Trans>
                </Dialog.Header>
                <div className={theme.deleteDialogBody}>
                    <Dialog.Description>
                        {isPublished ? (
                            <Trans>Option group is published. Unpublish before deleting.</Trans>
                        ) : (
                            <Trans>Are you sure you want to delete this option group?</Trans>
                        )}
                    </Dialog.Description>
                    <div className={theme.deleteDialogLists}>
                        <div>
                            <strong>
                                <Trans>Connected styles: </Trans>
                            </strong>
                            {stylesLoading ? (
                                <small>
                                    <Trans>Loading...</Trans>
                                </small>
                            ) : stylesUsingGroup.length > 0 ? (
                                <ul>
                                    {stylesUsingGroup.map((x, i) => (
                                        <li key={`${x.groupName}-${x.presetName}-${i}`}>
                                            {x.groupName} - {x.presetName}
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <small>
                                    <Trans>No styles depend on this option group.</Trans>
                                </small>
                            )}
                        </div>
                        <div>
                            <strong>
                                <Trans>Connected dependencies: </Trans>
                            </strong>
                            {dependenciesLoading ? (
                                <small>
                                    <Trans>Loading...</Trans>
                                </small>
                            ) : dependenciesUsingGroup.length > 0 ? (
                                <ul>
                                    {dependenciesUsingGroup.map((x) => (
                                        <li key={x.id}>{x.name}</li>
                                    ))}
                                </ul>
                            ) : (
                                <small>
                                    <Trans>No dependencies found.</Trans>
                                </small>
                            )}
                        </div>
                    </div>
                </div>
                <ButtonBar>
                    <ButtonBar.Right>
                        <Dialog.Close as={Button} variant="tertiary">
                            <Trans>Cancel</Trans>
                        </Dialog.Close>
                        <Button variant="danger" loading={deleteOptionGroup.isPending} onClick={handleDelete}>
                            <Trans>Delete</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </Dialog.Content>
        </Dialog>
    );
};
